import React, { useState, useEffect } from "react";
import axiosInstance from "../../../axiosInstance";
import "./CreatePayroll.scss";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";

const CreatePayroll = ({
  employee,
  closeModal,
  fetchEmployeeData,
  showToast,
}) => {
  const [grossSalary, setGrossSalary] = useState("");
  const [payrollType, setPayrollType] = useState("");
  const [payrollOptions, setPayrollOptions] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  useEffect(() => {
    const fetchPayrollOptions = async () => {
      try {
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/payroll/names`
        );
        setPayrollOptions(response.data.data);
      } catch (error) {
        console.error("Error fetching payroll options:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPayrollOptions();
  }, []);

  const validateComponents = () => {
    return components.every((component) => component.value !== "");
  };

  const handleGrossSalaryChange = (e) => {
    const value = e.target.value;
    setGrossSalary(value);
    setResponseData(null);
    setComponents([]);

    if (payrollType && value) {
      const selectedPayroll = payrollOptions.find(
        (option) => option.payrollName === payrollType
      );

      if (selectedPayroll?.payrollSetupType === "AUTO") {
        handleAutoPayroll(payrollType, value);
      } else if (selectedPayroll?.payrollSetupType === "MANUAL") {
        handleManualPayrollCalculate(payrollType, value);
      }
    }
  };

  const handlePayrollTypeChange = (e) => {
    const selectedPayrollType = e.target.value;
    setPayrollType(selectedPayrollType);
    setResponseData(null);
    setComponents([]);

    const selectedPayroll = payrollOptions.find(
      (option) => option.payrollName === selectedPayrollType
    );

    if (selectedPayroll?.payrollSetupType === "MANUAL" && grossSalary) {
      handleManualPayrollCalculate(selectedPayrollType, grossSalary);
    }

    if (selectedPayroll?.payrollSetupType === "AUTO" && grossSalary) {
      handleAutoPayroll(selectedPayrollType, grossSalary);
    }
  };

  const handleManualPayrollCalculate = async (payrollType, grossSalary) => {
    const selectedPayroll = payrollOptions.find(
      (option) => option.payrollName === payrollType
    );

    if (selectedPayroll && grossSalary) {
      setIsCalculating(true);
      try {
        const response = await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/Employee-Payroll/calculate`,
          {
            employeeId: employee.id,
            payrollId: selectedPayroll.payrollId,
            grossSalary: parseFloat(grossSalary),
            branchId: 0,
            employeePaymentSetupDTO: [],
          }
        );

        // Map components to empty input fields for manual entry
        const componentInputs =
          response.data.data.components?.map((comp) => ({
            ...comp,
            value: "", // Empty value for manual input
          })) || [];

        setComponents(componentInputs);
        setResponseData(response.data);
      } catch (error) {
        console.error("Manual payroll calculate error:", error);
        showToast(
          error.response?.data?.message ||
            "An error occurred while calculating payroll components. Please try again later.",
          "error"
        );
      } finally {
        setIsCalculating(false);
      }
    } else {
      console.error("Manual payroll calculate not called - missing data:", {
        selectedPayroll: !!selectedPayroll,
        grossSalary,
      });
    }
  };

  const handleAutoPayroll = async (payrollType, grossSalary) => {
    console.group("🔍 handleAutoPayroll Debug");

    const selectedPayroll = payrollOptions.find(
      (option) => option.payrollName === payrollType
    );

    if (selectedPayroll && grossSalary) {
      setIsSubmitting(true);
      console.info("✅ Conditions met: Proceeding with API call...");
      try {
        const payload = {
          employeeId: employee.id,
          payrollId: selectedPayroll.payrollId,
          grossSalary: parseFloat(grossSalary),
          branchId: 0,
          employeePaymentSetupDTO: [],
        };

        console.debug("📤 API Request Payload:", payload);
        const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Employee-Payroll/calculate`;
        console.debug("🌍 API URL:", apiUrl);

        const response = await axiosInstance.post(apiUrl, payload);

        console.info("✅ API Response Status:", response.status);
        console.debug("📥 Full API Response:", response);

        setComponents(response.data.data.components || []);
        setResponseData(response.data);
      } catch (error) {
        console.error("❌ Auto payroll error:", error);
        console.error("Error Details:", {
          message: error.message,
          responseData: error.response?.data,
          status: error.response?.status,
        });

        showToast(
          error.response?.data?.message ||
            "An error occurred. Please try again later.",
          "error"
        );
      } finally {
        console.info("🔄 Setting isSubmitting to false");
        setIsSubmitting(false);
        console.groupEnd();
      }
    } else {
      console.warn("❌ Auto payroll not called - Missing data:", {
        selectedPayroll: !!selectedPayroll,
        grossSalary,
      });
      console.groupEnd();
    }
  };

  const handleManualSubmit = async () => {
    const selectedPayroll = payrollOptions.find(
      (option) => option.payrollName === payrollType
    );

    if (!validateComponents()) {
      showToast("Please fill in all component values.", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      await axiosInstance.post(
        `${process.env.REACT_APP_API_BASE_URL}/Employee-Payroll/create`,
        {
          payrollId: selectedPayroll.payrollId,
          employeeId: employee.id,
          grossSalary: parseFloat(grossSalary),
          branchId: 0,
          employeePaymentSetupDTO: components.map((component) => ({
            paymentSetupId: component.paymentSetupId || 0,
            salaryName: component.salaryName,
            value: parseFloat(component.value),
          })),
        }
      );
      fetchEmployeeData();
      closeModal();
      showToast("Payroll created successfully!", "success");
    } catch (error) {
      console.error("Error creating payroll:", error);
      showToast(
        error.response?.data?.message ||
          "An error occurred. Please try again later.",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAutoSubmit = async () => {
    const selectedPayroll = payrollOptions.find(
      (option) => option.payrollName === payrollType
    );

    if (selectedPayroll && responseData) {
      setIsSubmitting(true);
      try {
        await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/Employee-Payroll/create`,
          {
            payrollId: selectedPayroll.payrollId,
            employeeId: employee.id,
            grossSalary: parseFloat(responseData.data.grossSalary),
            netSalary: parseFloat(responseData.data.netSalary),
            branchId: 0,
            employeeDeductions: parseFloat(
              responseData.data.employeeDeductions
            ),
            employerDeductions: parseFloat(
              responseData.data.employerDeductions
            ),
            ctc: parseFloat(responseData.data.ctc),
            employeePaymentSetupDTO: [],
          }
        );
        fetchEmployeeData();
        closeModal();
        showToast("Payroll created successfully!", "success");
      } catch (error) {
        console.error("Error submitting automatic payroll:", error);
        showToast(
          error.response?.data?.message ||
            "An error occurred. Please try again later.",
          "error"
        );
      } finally {
        setIsSubmitting(false);
      }
    } else {
      showToast("Please calculate payroll before submitting.", "error");
    }
  };

  const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const renderComponentsInputs = () =>
    components.map((component, index) => (
      <div key={index} className="component-input">
        <label>
          {component.salaryName} - {capitalize(component.contributionType)}:
        </label>
        <input
          type="number"
          value={component.value || ""}
          placeholder="Enter amount"
          onChange={(e) => {
            const updatedComponents = [...components];
            updatedComponents[index].value = e.target.value;
            setComponents(updatedComponents);
          }}
        />
      </div>
    ));

  const renderAutoComponents = () =>
    components.map((component, index) => (
      <div key={index} className="salary-component-row">
        <span className="salary-name">{component.salaryName}</span>

        <span className="salary-meta">
          {capitalize(component.contributionType)}
        </span>

        <span className="salary-amount">₹{component.amount.toFixed(2)}</span>
      </div>
    ));

  if (!employee) {
    return <div>Loading employee data...</div>;
  }

  return (
    <div className="create-payroll-form">
      <h1>Create Payroll for {employee.name}</h1>

      <div>
        <label htmlFor="grossSalary">Gross Salary:</label>
        <input
          id="grossSalary"
          type="number"
          placeholder="Enter gross salary"
          value={grossSalary}
          onChange={handleGrossSalaryChange}
        />
      </div>

      <div>
        <label htmlFor="payrollType">Payroll Type:</label>
        {loading ? (
          <LoadingScreen />
        ) : (
          <select
            id="payrollType"
            value={payrollType}
            onChange={handlePayrollTypeChange}
            disabled={!grossSalary}
          >
            <option value="">Select payroll type...</option>
            {payrollOptions.map((option) => (
              <option key={option.payrollId} value={option.payrollName}>
                {option.payrollName || "Unnamed Type"} -{" "}
                {option.payrollSetupType}
              </option>
            ))}
          </select>
        )}
      </div>

      {payrollType &&
        payrollOptions.find((o) => o.payrollName === payrollType)
          ?.payrollSetupType === "AUTO" && (
          <div>
            {isSubmitting && <div>Calculating payroll...</div>}
            {responseData && (
              <div className="auto-payroll-summary">
                <h3>Payroll Summary</h3>
                {responseData?.data?.grossSalary !== undefined && (
                  <div className="summary-item">
                    <strong>Gross Salary:</strong> ₹
                    {responseData.data.grossSalary}
                  </div>
                )}
                <div className="auto-components">
                  <h3>Salary Components</h3>
                  {renderAutoComponents()}
                </div>

                {responseData?.data?.netSalary !== undefined && (
                  <div className="summary-item">
                    <strong>Net Salary:</strong> ₹{responseData.data.netSalary}
                  </div>
                )}

                {responseData?.data?.employeeDeductions !== undefined && (
                  <div className="summary-item">
                    <strong>Employee Deductions:</strong> ₹
                    {responseData.data.employeeDeductions}
                  </div>
                )}

                {responseData?.data?.employerDeductions !== undefined && (
                  <div className="summary-item">
                    <strong>Employer Deductions:</strong> ₹
                    {responseData.data.employerDeductions}
                  </div>
                )}

                {responseData?.data?.ctc !== undefined && (
                  <div className="summary-item">
                    <strong>CTC:</strong> ₹{responseData.data.ctc}
                  </div>
                )}

                <ButtonWithIcon
                  text={isSubmitting ? "Submitting..." : "Submit Payroll"}
                  onClick={handleAutoSubmit}
                  disabled={isSubmitting}
                  className="success"
                />
              </div>
            )}
          </div>
        )}

      {payrollType &&
        payrollOptions.find((o) => o.payrollName === payrollType)
          ?.payrollSetupType === "MANUAL" && (
          <div className="manual-payroll-inputs">
            {isCalculating && <div>Loading payroll components...</div>}
            {components.length > 0 && (
              <>
                {renderComponentsInputs()}
                <ButtonWithIcon
                  text={isSubmitting ? "Submitting..." : "Submit Payroll"}
                  onClick={handleManualSubmit}
                  disabled={isSubmitting || !validateComponents()}
                  className="success"
                />
              </>
            )}
          </div>
        )}
    </div>
  );
};

export default CreatePayroll;
